(() => {
  if (customElements.get('ff-shop')) return;
  const FREE_DELIVERY = 3000; // pence
  const money = (p) => '£' + (p / 100).toFixed(2);

  customElements.define('ff-shop', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      // While previewing an unpublished theme, keep every shop link inside this preview
      const theme = window.Shopify && window.Shopify.theme;
      if (theme && theme.role !== 'main' && theme.id) {
        this.querySelectorAll('a[href^="/"]').forEach((a) => {
          const u = new URL(a.href, location.origin);
          u.searchParams.set('preview_theme_id', theme.id);
          a.href = u.pathname + u.search + u.hash;
        });
      }

      // Mixer filter
      const bar = this.querySelector('[data-filter-bar]');
      if (bar) {
        bar.hidden = false;
        const cards = [...this.querySelectorAll('[data-card]')];
        bar.addEventListener('click', (e) => {
          const b = e.target.closest('[data-filter]');
          if (!b) return;
          const kind = b.dataset.filter;
          cards.forEach((c) => { c.hidden = kind !== 'all' && c.dataset.kind !== kind; });
          bar.querySelectorAll('[data-filter]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        });
      }

      // Drink tabs stay at the top; on Shop All they jump to each section
      const spy = [...this.querySelectorAll('[data-spy]')];
      if (spy.length) {
        const targets = spy.map((a) => document.querySelector(a.getAttribute('href'))).filter(Boolean);
        const setActive = (id) => spy.forEach((a) => {
          if (a.getAttribute('href') === '#' + id) {
            if (!a.hasAttribute('aria-current')) {
              a.setAttribute('aria-current', 'page');
              const row = a.parentElement;
              row.scrollTo({ left: a.offsetLeft - row.offsetLeft - 8, behavior: 'smooth' });
            }
          } else a.removeAttribute('aria-current');
        });
        let lockUntil = 0;
        const topOffset = () => window.matchMedia('(max-width:749px)').matches ? 16 : this.querySelector('.ffs-nav').offsetHeight + 16;
        this.addEventListener('click', (e) => {
          const a = e.target.closest('a[href^="#ffs-"]');
          if (!a) return;
          const t = document.querySelector(a.getAttribute('href'));
          if (!t) return;
          e.preventDefault();
          // Keep the tapped tab highlighted while the page glides there
          lockUntil = Date.now() + 1100;
          window.scrollTo({ top: t.getBoundingClientRect().top + window.scrollY - topOffset(), behavior: 'smooth' });
          setActive(t.id);
        });
        const onScroll = () => {
          if (Date.now() < lockUntil) return;
          const line = this.querySelector('.ffs-nav').offsetHeight + 40;
          let current = null;
          let best = -Infinity;
          targets.forEach((t) => {
            const top = t.getBoundingClientRect().top - line;
            if (top <= 0 && top > best) { best = top; current = t; }
          });
          if (current) setActive(current.id);
          else spy.forEach((a) => a.removeAttribute('aria-current'));
        };
        window.addEventListener('scroll', () => requestAnimationFrame(onScroll), { passive: true });

        // Old addresses (e.g. /collections/natural-sodas) open this page at their own section
        const start = this.dataset.start;
        const startTarget = start && start !== 'all' ? document.getElementById('ffs-g-' + start) : null;
        if (startTarget) {
          let userMoved = false;
          ['wheel', 'touchstart', 'keydown'].forEach((ev) => window.addEventListener(ev, () => { userMoved = true; }, { once: true, passive: true }));
          const go = () => {
            if (userMoved) return;
            const navH = this.querySelector('.ffs-nav').offsetHeight;
            window.scrollTo({ top: startTarget.getBoundingClientRect().top + window.scrollY - navH - 16, behavior: 'instant' });
            setActive(startTarget.id);
          };
          requestAnimationFrame(go);
          window.addEventListener('load', go, { once: true });
        } else {
          onScroll();
        }
      }
      const basket = this.querySelector('[data-basket]');
      if (basket) basket.addEventListener('click', () => {
        const trigger = document.querySelector('xo-modal-trigger[xo-name="cart"]');
        if (trigger) trigger.click(); else location.href = '/cart';
      });

      // Bundle tabs (mobile shows one bundle at a time)
      this.querySelectorAll('[data-bundles]').forEach((wrap) => {
        const tabs = [...wrap.querySelectorAll('[data-btab]')];
        const cards = [...wrap.querySelectorAll('[data-bcard]')];
        tabs.forEach((t, i) => t.addEventListener('click', () => {
          tabs.forEach((x, j) => x.setAttribute('aria-pressed', String(i === j)));
          cards.forEach((c, j) => c.classList.toggle('is-active', i === j));
        }));
      });

      this.addEventListener('click', (e) => {
        // Quantity stepper
        const step = e.target.closest('[data-step]');
        if (step) {
          const input = step.parentElement.querySelector('input');
          const next = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.step)));
          input.value = next;
          return;
        }
      });

      // Pack size pills
      this.addEventListener('change', (e) => {
        const input = e.target.closest('.ffs-pill input');
        if (!input) return;
        const card = input.closest('[data-card]');
        card.querySelector('.ffs-card__price [data-price]').textContent = input.dataset.price;
        card.querySelector('.ffs-card__price [data-unit]').textContent = input.dataset.unit;
        const pack = card.querySelector('[data-pack]');
        if (pack) pack.textContent = input.dataset.title;
        const btn = card.querySelector('[data-add]');
        btn.disabled = input.dataset.available !== 'true';
        btn.textContent = btn.disabled ? 'Sold out' : 'Add to basket';
        card.querySelectorAll('[data-link]').forEach((a) => {
          const u = new URL(a.href); u.searchParams.set('variant', input.value); a.href = u.href;
        });
        const img = card.querySelector('img');
        if (img && input.dataset.image) { img.removeAttribute('srcset'); img.src = input.dataset.image; }
      });

      // Add to basket: stay on the page, confirm with a small note
      this.addEventListener('submit', async (e) => {
        const form = e.target.closest('[data-add-form]');
        if (!form || !window.fetch) return;
        e.preventDefault();
        const btn = form.querySelector('[data-add]');
        const label = btn.textContent;
        const qty = parseInt(form.querySelector('[name="quantity"]').value, 10) || 1;
        const name = form.closest('[data-card]').querySelector('.ffs-card__title').textContent.trim();
        btn.disabled = true; btn.textContent = 'Adding…';
        try {
          const res = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!res.ok) throw new Error('add failed');
          const cart = await this.refreshCart();
          btn.classList.add('is-added'); btn.textContent = 'Added ✓';
          this.toast(`${qty} × ${name}`, cart);
          form.querySelector('[name="quantity"]').value = 1;
          setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = label; btn.disabled = false; }, 1800);
        } catch (err) {
          btn.disabled = false; btn.textContent = label;
          form.submit();
        }
      });
    }

    toast(line, cart) {
      let t = document.querySelector('.ffs-toast');
      if (!t) {
        t = document.createElement('div');
        t.className = 'ffs-toast';
        t.setAttribute('role', 'status');
        t.setAttribute('aria-live', 'polite');
        document.body.appendChild(t);
      }
      let note = '';
      if (cart) {
        const left = FREE_DELIVERY - cart.total_price;
        note = left > 0 ? `Add ${money(left)} more for free delivery` : 'You have free delivery';
      }
      t.innerHTML = `<div><strong>Added to your basket</strong><span>${line.replace(/</g, '&lt;')}</span>${note ? `<em>${note}</em>` : ''}</div><button type="button" data-view>View basket</button><button type="button" class="ffs-toast__x" aria-label="Close" data-close>×</button>`;
      t.classList.add('is-on');
      clearTimeout(this._t);
      this._t = setTimeout(() => t.classList.remove('is-on'), 6000);
      t.querySelector('[data-close]').onclick = () => t.classList.remove('is-on');
      t.querySelector('[data-view]').onclick = () => {
        t.classList.remove('is-on');
        const trigger = document.querySelector('xo-modal-trigger[xo-name="cart"]');
        if (trigger) trigger.click(); else location.href = '/cart';
      };
    }

    async refreshCart() {
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        document.querySelectorAll('xo-cart-size').forEach((el) => { el.textContent = cart.item_count; });
        const section = document.querySelector('.section-xo-cart-mini[id^="shopify-section-"]');
        if (section) {
          const id = section.id.replace('shopify-section-', '');
          const data = await fetch(`${location.pathname}?sections=${encodeURIComponent(id)}`).then((r) => r.json());
          if (data[id]) {
            const doc = new DOMParser().parseFromString(data[id], 'text/html');
            const fresh = doc.querySelector('xo-cart-mini');
            const current = document.querySelector('xo-cart-mini');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          }
        }
        return cart;
      } catch (e) { return null; }
    }
  });
})();
